import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiErrorState } from '../../components/ApiErrorState.tsx';
import { useAuth } from '../auth/useAuth.ts';
import { useMatchPredictions } from './hooks.ts';

/**
 * Pronósticos del resto de la liga para un partido ya empezado.
 *
 * Se carga bajo demanda, al desplegar: en una jornada con catorce partidos, pedirlos
 * todos de golpe serían catorce peticiones para mirar dos.
 *
 * Solo se monta cuando el servidor ha dicho que el partido empezó (`is_open === false`).
 * Antes del kickoff el endpoint responde 409 a propósito y no devuelve nada, ni siquiera
 * cuántos pronósticos hay (ADR 0006).
 */
export function LeagueMatchPredictions({
  leagueId,
  matchId,
}: {
  leagueId: string;
  matchId: number;
}) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [abierto, setAbierto] = useState(false);
  const { data, isPending, error } = useMatchPredictions(leagueId, matchId, abierto);

  return (
    <section className="mt-3 border-t border-borde pt-3">
      <button
        type="button"
        className="text-sm text-acento underline"
        onClick={() => setAbierto((v) => !v)}
        aria-expanded={abierto}
        data-test="ver-ajenos"
      >
        {abierto ? t('predictions.others.hide') : t('predictions.others.show')}
      </button>

      {abierto ? (
        <div className="mt-2">
          <ApiErrorState error={error} />
          {isPending && !error ? (
            <p className="text-sm text-tenue">{t('common.loading')}</p>
          ) : null}
          <ul className="list-none space-y-1 p-0" data-test="ajenos">
            {data?.map((p) => (
              <li
                key={p.user_id}
                className="flex items-baseline justify-between gap-3 text-sm"
                data-test="ajeno"
                data-username={p.username}
              >
                <span className="min-w-0 truncate">
                  {p.username}
                  {p.user_id === user?.id ? (
                    <span className="text-tenue"> ({t('standings.you')})</span>
                  ) : null}
                  {!p.counts_for_league ? (
                    <span className="text-xs text-tenue">
                      {' '}
                      — {t('predictions.others.notCounted')}
                    </span>
                  ) : null}
                </span>
                <span className="shrink-0 font-mono tabular-nums">
                  {p.predicted_home_score === null
                    ? t('predictions.others.noPrediction')
                    : `${p.predicted_home_score} – ${p.predicted_away_score}`}
                </span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </section>
  );
}
